import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BookingForgotPasswordVerifyResponse } from 'src/app/models/booking/booking-auth.model';
import { AuthTexts } from 'src/app/models/settings/setting.model';
import { BookingAuthService } from 'src/app/services/booking-auth.service';
import { SettingsService } from 'src/app/services/settings.service';

type BookingAuthDialogMode =
  | 'login'
  | 'register'
  | 'validateEmail'
  | 'forgotPassword'
  | 'verifyResetCode'
  | 'resetPassword';

type BookingAuthDialogErrorKey =
  | 'invalidLogin'
  | 'accountExists'
  | 'invalidRequest'
  | 'invalidValidationCode'
  | 'invalidPasswordResetCode'
  | 'invalidPasswordResetToken'
  | 'requestFailed';

@Component({
  selector: 'app-booking-auth-dialog',
  host: {
    '[class.booking-auth-inline]': "presentation() === 'inline'",
  },
  imports: [ReactiveFormsModule],
  templateUrl: './booking-auth-dialog.component.html',
  styleUrls: ['./booking-auth-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingAuthDialogComponent {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly bookingAuthService = inject(BookingAuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly fallbackTexts = new AuthTexts();

  readonly closed = output<void>();
  readonly authenticated = output<void>();
  readonly presentation = input<'dialog' | 'inline'>('dialog');
  readonly initialMode = input<BookingAuthDialogMode>('login');
  readonly validationEmail = input('');

  readonly mode = signal<BookingAuthDialogMode>('login');
  readonly pendingValidationEmail = signal('');
  readonly isSubmitting = signal(false);
  readonly isResendingValidationEmail = signal(false);
  readonly validationEmailSent = signal(false);
  readonly passwordResetEmail = signal('');
  readonly passwordResetVerification =
    signal<BookingForgotPasswordVerifyResponse | null>(null);
  readonly passwordResetEmailSent = signal(false);
  readonly passwordResetCompleted = signal(false);
  readonly errorMessageKey = signal<BookingAuthDialogErrorKey | null>(null);
  readonly isBusy = computed(
    () => this.isSubmitting() || this.isResendingValidationEmail(),
  );
  readonly isDialog = computed(() => this.presentation() === 'dialog');
  readonly isOverlayCloseDisabled = computed(
    () => this.isBusy() || this.mode() === 'validateEmail',
  );
  readonly texts = computed(
    () =>
      ({
        ...this.fallbackTexts,
        ...this.settingsService.setting().AuthTexts,
      }) as AuthTexts,
  );
  readonly dialogTitle = computed(() => {
    const texts = this.texts();

    switch (this.mode()) {
      case 'register':
        return texts.CreateAccount;
      case 'validateEmail':
        return texts.ValidateEmail;
      case 'forgotPassword':
      case 'verifyResetCode':
      case 'resetPassword':
        return texts.ForgotPasswordHeader;
      case 'login':
      default:
        return texts.Login;
    }
  });
  readonly showAccountModeTabs = computed(
    () => this.mode() === 'login' || this.mode() === 'register',
  );

  readonly loginForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  readonly registerForm = this.formBuilder.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    mobilePhoneNumber: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  readonly validationCodeForm = this.formBuilder.group({
    code: [
      '',
      [
        Validators.required,
        Validators.minLength(4),
        Validators.pattern(/^\d{4}$/),
      ],
    ],
  });

  readonly forgotPasswordForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
  });

  readonly passwordResetCodeForm = this.formBuilder.group({
    code: [
      '',
      [
        Validators.required,
        Validators.minLength(6),
        Validators.pattern(/^\d{6}$/),
      ],
    ],
  });

  readonly resetPasswordForm = this.formBuilder.group({
    newPassword: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor() {
    effect(() => {
      const initialMode = this.initialMode();

      this.mode.set(initialMode);
      this.errorMessageKey.set(null);
      this.validationEmailSent.set(false);
      this.resetPasswordFlow();

      if (initialMode !== 'validateEmail') {
        return;
      }

      this.pendingValidationEmail.set(this.validationEmail().trim());
      this.validationCodeForm.reset({ code: '' });
    });
  }

  switchMode(mode: 'login' | 'register'): void {
    if (this.isSubmitting()) {
      return;
    }

    this.mode.set(mode);
    this.pendingValidationEmail.set('');
    this.validationCodeForm.reset({ code: '' });
    this.resetPasswordFlow();
    this.errorMessageKey.set(null);
    this.validationEmailSent.set(false);
  }

  startForgotPassword(): void {
    if (this.isBusy()) {
      return;
    }

    this.forgotPasswordForm.reset({
      email: this.loginForm.controls.email.value.trim(),
    });
    this.passwordResetCodeForm.reset({ code: '' });
    this.resetPasswordForm.reset({ newPassword: '' });
    this.passwordResetEmail.set('');
    this.passwordResetVerification.set(null);
    this.passwordResetEmailSent.set(false);
    this.passwordResetCompleted.set(false);
    this.errorMessageKey.set(null);
    this.mode.set('forgotPassword');
  }

  returnToLogin(): void {
    if (this.isBusy()) {
      return;
    }

    const email =
      this.passwordResetEmail() ||
      this.forgotPasswordForm.controls.email.value.trim();

    this.resetPasswordFlow();
    this.loginForm.reset({ email, password: '' });
    this.errorMessageKey.set(null);
    this.mode.set('login');
  }

  handleOverlayClick(): void {
    if (!this.isOverlayCloseDisabled()) {
      this.closeDialog();
    }
  }

  closeDialog(): void {
    if (this.isDialog()) {
      this.closed.emit();
    }
  }

  async submitLogin(): Promise<void> {
    const email = this.loginForm.value['email'];
    const password = this.loginForm.value['password'];

    if (!email || !password || this.isSubmitting()) {
      this.loginForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);
    this.passwordResetCompleted.set(false);

    try {
      await this.bookingAuthService.login(this.loginForm.getRawValue());
      this.authenticated.emit();
      this.closed.emit();
    } catch (error) {
      this.errorMessageKey.set(this.getErrorMessageKey(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async submitForgotPassword(): Promise<void> {
    if (this.forgotPasswordForm.invalid || this.isSubmitting()) {
      this.forgotPasswordForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    const email = this.forgotPasswordForm.controls.email.value.trim();

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);
    this.passwordResetEmailSent.set(false);
    this.passwordResetCompleted.set(false);

    try {
      const passwordResetEmailSent =
        await this.bookingAuthService.forgotPassword({ email });

      if (!passwordResetEmailSent) {
        throw new Error('Password reset email was not sent.');
      }

      this.passwordResetEmail.set(email);
      this.passwordResetCodeForm.reset({ code: '' });
      this.passwordResetEmailSent.set(true);
      this.mode.set('verifyResetCode');
    } catch (_error) {
      this.errorMessageKey.set('requestFailed');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async submitPasswordResetCode(): Promise<void> {
    const email =
      this.passwordResetEmail() ||
      this.forgotPasswordForm.controls.email.value.trim();
    const code = this.passwordResetCodeForm.controls.code.value.trim();

    if (this.isSubmitting()) {
      return;
    }

    if (!email || this.passwordResetCodeForm.invalid) {
      this.passwordResetCodeForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    try {
      const verification =
        await this.bookingAuthService.verifyForgotPasswordCode({
          email,
          code,
        });

      this.passwordResetVerification.set(verification);
      this.resetPasswordForm.reset({ newPassword: '' });
      this.passwordResetEmailSent.set(false);
      this.mode.set('resetPassword');
    } catch (error) {
      this.errorMessageKey.set(this.getErrorMessageKey(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async submitResetPassword(): Promise<void> {
    const verification = this.passwordResetVerification();

    if (this.isSubmitting()) {
      return;
    }

    if (!verification || this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    try {
      const passwordReset = await this.bookingAuthService.resetPassword({
        resetRequestId: verification.resetRequestId,
        resetToken: verification.resetToken,
        newPassword: this.resetPasswordForm.controls.newPassword.value,
      });

      if (!passwordReset) {
        throw new Error('Password was not reset.');
      }

      const email = this.passwordResetEmail();

      this.resetPasswordFlow();
      this.loginForm.reset({ email, password: '' });
      this.passwordResetCompleted.set(true);
      this.mode.set('login');
    } catch (error) {
      this.errorMessageKey.set(this.getErrorMessageKey(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async submitRegister(): Promise<void> {
    if (this.registerForm.invalid || this.isSubmitting()) {
      this.registerForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    try {
      await this.bookingAuthService.register(this.registerForm.getRawValue());
      this.pendingValidationEmail.set(this.registerForm.controls.email.value);
      this.validationCodeForm.reset({ code: '' });
      this.validationEmailSent.set(false);
      this.mode.set('validateEmail');
    } catch (error) {
      this.errorMessageKey.set(this.getErrorMessageKey(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async submitValidationCode(): Promise<void> {
    const email =
      this.pendingValidationEmail() || this.registerForm.controls.email.value;
    const code = this.validationCodeForm.controls.code.value.trim();

    if (this.isBusy()) {
      return;
    }

    if (!email || this.validationCodeForm.invalid) {
      this.validationCodeForm.markAllAsTouched();
      this.errorMessageKey.set('invalidRequest');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    try {
      await this.bookingAuthService.validateEmailCode({
        email,
        EmailValidationCode: code,
      });

      if (!this.bookingAuthService.isLoggedIn()) {
        await this.bookingAuthService.login({
          email,
          password: this.registerForm.controls.password.value,
        });
      }

      this.authenticated.emit();
      this.closed.emit();
    } catch (error) {
      this.errorMessageKey.set(this.getErrorMessageKey(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  async resendValidationEmail(): Promise<void> {
    if (this.isBusy()) {
      return;
    }

    this.isResendingValidationEmail.set(true);
    this.validationEmailSent.set(false);
    this.errorMessageKey.set(null);

    try {
      await this.bookingAuthService.resendValidationEmail();
      this.validationEmailSent.set(true);
    } catch (_error) {
      this.errorMessageKey.set('requestFailed');
    } finally {
      this.isResendingValidationEmail.set(false);
    }
  }

  getDialogErrorMessage(errorKey: BookingAuthDialogErrorKey): string {
    const texts = this.texts();

    switch (errorKey) {
      case 'invalidLogin':
        return texts.InvalidLogin;
      case 'accountExists':
        return texts.AccountExists;
      case 'invalidRequest':
        return texts.InvalidRequest;
      case 'invalidValidationCode':
        return texts.InvalidValidationCode;
      case 'invalidPasswordResetCode':
        return texts.InvalidPasswordResetCode;
      case 'invalidPasswordResetToken':
        return texts.InvalidPasswordResetToken;
      case 'requestFailed':
      default:
        return texts.RequestCouldNotBeCompleted;
    }
  }

  private getErrorMessageKey(error: unknown): BookingAuthDialogErrorKey {
    if (!(error instanceof HttpErrorResponse)) {
      if (this.mode() === 'verifyResetCode') {
        return 'invalidPasswordResetCode';
      }

      if (this.mode() === 'resetPassword') {
        return 'invalidPasswordResetToken';
      }

      return 'requestFailed';
    }

    if (error.status === 401) {
      return this.mode() === 'login'
        ? 'invalidLogin'
        : 'invalidPasswordResetToken';
    }

    if (error.status === 409) {
      return 'accountExists';
    }

    if (error.status === 400 || error.status === 404) {
      switch (this.mode()) {
        case 'validateEmail':
          return 'invalidValidationCode';
        case 'verifyResetCode':
          return 'invalidPasswordResetCode';
        case 'resetPassword':
          return 'invalidPasswordResetToken';
        default:
          return 'invalidRequest';
      }
    }

    if (error.status === 410) {
      return 'invalidPasswordResetToken';
    }

    return 'requestFailed';
  }

  private resetPasswordFlow(): void {
    this.passwordResetEmail.set('');
    this.passwordResetVerification.set(null);
    this.passwordResetEmailSent.set(false);
    this.passwordResetCompleted.set(false);
    this.forgotPasswordForm.reset({ email: '' });
    this.passwordResetCodeForm.reset({ code: '' });
    this.resetPasswordForm.reset({ newPassword: '' });
  }
}
